import FeatherIcon from "feather-icons-react";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getApplicationById } from "../../core/axios/endpoints";
import PageLoader from "../../components/page-loader";
import { APPLICATION_STATUS, NETWORK_ERROR, RoutesFullPath } from "../../utils/constants";

// Jira-lozenge per aanvraagstatus (semantische kleuren uit de designbrief).
const STATUS_LOZENGE = {
    "In-behandeling": "bg-[#FFF7D6] text-[#7F5F01]",
    "Ingediend-bij-rvo": "bg-brand-subtle text-brand",
    Goedgekeurd: "bg-[#DCFFF1] text-[#1F845A]",
    Afgekeurd: "bg-[#FFECEB] text-[#AE2A19]",
};

const Lozenge = ({ text, className = "" }) => (
    <span className={`inline-flex items-center rounded px-1.5 py-px text-[11px] font-bold uppercase tracking-wide ${className}`}>
        {text}
    </span>
);

const Section = ({ title, children, className = "" }) => (
    <section className={`rounded bg-white p-5 shadow-card ${className}`}>
        <h2 className="mb-3 text-base font-semibold text-ink">{title}</h2>
        {children}
    </section>
);

const Field = ({ label, children }) => (
    <div>
        <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">{label}</dt>
        <dd className="mt-0.5 text-sm text-ink">{children}</dd>
    </div>
);

const fileButtonClass = "inline-flex h-8 max-w-full items-center gap-1.5 rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224]";

const ClientAanvraagDetailsPage = (props) => {
    const [AanvraagDetails, setAanvraagDetails] = useState({});
    const [isLoading, setLoading] = useState(false);
    const { aanraagId } = useParams();
    const [isError, setError] = useState(false);
    const [isNetworkError, setIsNetworkError] = useState(false);
    const navigate = useNavigate();
    const getAanraagDetails = async () => {
        setLoading(true);
        await getApplicationById(aanraagId).then((data) => {
            setAanvraagDetails(data);
            
        }).catch(err => {
            setError(true);
            if(err?.isNetworkError){
                setIsNetworkError(true);
            }
        }).finally(() => {
            setLoading(false);
        });
    };

    useEffect(() => {
        getAanraagDetails();
    }, []);

    const editApplication = () => {
        if(AanvraagDetails?.regling === "EIA Zon" || AanvraagDetails?.regling === "EIA"){
        // Eén EIA-formulier; de keuze zonnepanelen/anders volgt uit de regeling.
        navigate(RoutesFullPath.EIAAanraag,{state:{applicationId:aanraagId}});
        }else if(AanvraagDetails?.regling === "ISDE"){
        navigate(RoutesFullPath.ISDEAanvragen,{state:{applicationId:aanraagId}});
        }
      };

      const formatNumber = (number) => {
        return number.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      };

      function formatDate(dateString) {
        const [year, month, day] = dateString.split('-');
        return `${day}-${month}-${year}`;
      }
    

    if (isLoading) {
        return <PageLoader />;
    }

    if (isError) {
        return (
            <div className="main-wrapper">
                <div className="error-box">
                    <h1>{isNetworkError ? "503" : "404" }</h1>
                    <h3 className="h2 mb-3">
                        <i className="fas fa-exclamation-circle" /> Oops! {isNetworkError ? "Network Error!" : ""}
                    </h3>
                    <p className="h4 font-weight-normal mt-2">
                        {isNetworkError ? NETWORK_ERROR : "Er is iets misgegaan. Probeer het later opnieuw."}
                    </p>
                    <button onClick={() => navigate(-1)} className="customized-btn mt-6">
                       Go Back
                    </button>
                </div>
            </div>
        );
    }
    return (
        <>
            <div className="page-wrapper">
                <div className="content aanvraag-details-page">
                    {/* Paginakop: breadcrumb, titel, status */}
                    <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
                        <div>
                            <nav className="mb-1 text-xs text-ink-subtlest">
                                <Link to={RoutesFullPath.customerDashboard} className="hover:underline">Aanvragen</Link>
                                <span className="mx-1">/</span>
                                <span>{AanvraagDetails?.Kenmerk}</span>
                            </nav>
                            <h1 className="text-2xl font-medium text-ink">
                                {AanvraagDetails?.VoorLetters + " " + AanvraagDetails?.achterNaam}
                            </h1>
                            <dl className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                                <div className="flex items-baseline gap-1.5">
                                    <dt className="text-ink-subtlest">Kenmerk klant</dt>
                                    <dd className="font-medium text-ink">{AanvraagDetails?.Kenmerk}</dd>
                                </div>
                                <div className="flex items-baseline gap-1.5">
                                    <dt className="text-ink-subtlest">Klantnaam</dt>
                                    <dd className="font-medium text-ink">{AanvraagDetails?.VoorLetters + " " + AanvraagDetails?.achterNaam}</dd>
                                </div>
                            </dl>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs text-ink-subtlest">Status van aanvraag</span>
                            <Lozenge
                                text={AanvraagDetails?.status}
                                className={STATUS_LOZENGE[AanvraagDetails?.status] ?? "bg-[#091E420F] text-ink-subtle"}
                            />
                            <Lozenge text={AanvraagDetails?.regling} className="bg-brand-subtle text-brand" />
                        </div>
                    </header>

                    { AanvraagDetails?.status == APPLICATION_STATUS.AFGEKEURD &&  <div className="mb-6 flex flex-wrap items-start justify-between gap-3 rounded bg-[#FFECEB] px-3 py-2 text-sm text-[#AE2A19]">
                        <div className="flex items-start gap-2">
                            <FeatherIcon icon="alert-circle" size={16} className="mt-0.5 shrink-0" />
                            <div>
                                <p className="font-semibold">Reden voor Afgekeurdt</p>
                                <p>{AanvraagDetails?.afgekeurdReason}</p>
                            </div>
                        </div>
                        <button onClick={editApplication} className="inline-flex h-8 items-center justify-center rounded bg-brand px-3 text-sm font-medium text-white hover:bg-brand-hover disabled:pointer-events-none disabled:opacity-50">
                        Opnieuw indienen
                            </button>

                    </div>}

                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        <Section title="Persoonlijke gegevens">
                            <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                                <Field label="Naam">{
                                    AanvraagDetails?.VoorLetters + " " + AanvraagDetails?.achterNaam
                                    }</Field>
                                <Field label="Telefoonnummer">{AanvraagDetails?.teleFoonNummer}</Field>
                                <Field label="Geslacht">{AanvraagDetails?.geslacht}</Field>
                                <Field label="E-mail">{AanvraagDetails?.email}</Field>
                                <Field label="Postcode">{AanvraagDetails?.postCode}</Field>
                                <Field label="KVK-nummer">{AanvraagDetails?.KvkNummer}</Field>
                                <Field label="Woonplaats">{AanvraagDetails?.plaats}</Field>
                                <Field label="Handelsnaam">{AanvraagDetails?.handelsNaam}</Field>
                                <Field label="Adres">{AanvraagDetails?.straatNaam} {AanvraagDetails?.huisNummer}</Field>
                                {
                                    ["EIA", "EIA Zon"].includes(AanvraagDetails?.regling) && <Field label="Bedrijfsmiddelcodes">
                                    {
                                        AanvraagDetails?.bedrijfsmiddelCode?.map((item, index) => {
                                            return <p key={index}>{item}</p>
                                        })
                                    }
                                </Field>
                                }
                               {
                                AanvraagDetails?.regling === "ISDE" && <Field label="meldCodes">
                                    {
                                        AanvraagDetails?.meldCodes?.map((item, index) => {
                                            return <p key={index}>{item}</p>
                                        })
                                    }
                                </Field>
                                }
                            </dl>
                        </Section>

                        <Section title={`Subsidie (${AanvraagDetails?.regling})`}>
                            <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                                {AanvraagDetails?.regling === "EIA Zon" && (
                                    <>
                                        <Field label="Datum opdrachtbevestiging">{ formatDate(AanvraagDetails?.datumOpdrachtBevestiging) }</Field>
                                        <Field label="Aantal zonnepanelen">{AanvraagDetails?.aantalZonnepanelen}</Field>
                                        <Field label="Totaal vermogen (Wp)">{AanvraagDetails?.totaalVermogen}</Field>
                                    </>
                                )}
                                {["EIA", "ISDE"].includes(AanvraagDetails?.regling) && (
                                    <Field label="Datum opdrachtbevestiging">{ formatDate(AanvraagDetails?.datumOpdrachtBevestiging) || '' }</Field>
                                )}
                                {AanvraagDetails?.regling === "ISDE" && (
                                    <Field label="Type">{AanvraagDetails?.type}</Field>
                                )}
                            </dl>
                            { AanvraagDetails?.regling === "ISDE" && AanvraagDetails?.type === 'warmtepomp' && <div className="mt-4">
                                <a
                                    href={AanvraagDetails?.annualStatement}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={fileButtonClass}
                                >
                                    <FeatherIcon icon="download" size={16} />
                                    Jaarnota
                                </a>
                            </div>
                            }
                            {
                               !!AanvraagDetails?.quotationCopyFiles?.length && 
                              <div className="mt-4 border-t border-line pt-4">
                              <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">Offerte</p>
                            <div className="mt-2 flex flex-row flex-wrap gap-2">
                                {AanvraagDetails?.quotationCopyFiles?.map((url, index) => {
                                    const fileName = url.split('/').pop();
                                    return <a
                                        key={index}
                                        href={url} // Replace with the actual base URL or path where your files are stored
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={fileButtonClass}
                                        title={fileName}
                                    >
                                        <FeatherIcon icon="download" size={16} /> {fileName?.length > 20 ? fileName?.slice(0, 15) + "..." : fileName}
                                    </a>
                                })}
                            </div>
                            </div>
                            }
                        </Section>

                        <Section title="Aantekeningen (Intern)">
                            <p className="text-sm text-ink">{AanvraagDetails?.aantekeningVoorOns || "Geen aantekingen"}</p>
                        </Section>

                        <Section title="Factuurinformatie">
                            {AanvraagDetails?.billingMode === "partner_invoice" ? (
                                <div className="text-sm text-ink">
                                    <p>Deze aanvraag loopt via uw installateur{AanvraagDetails?.partnerUserId?.bedrijfsnaam ? <> (<strong>{AanvraagDetails.partnerUserId.bedrijfsnaam}</strong>)</> : null}.</p>
                                    <p className="mt-1 text-sm text-ink-subtlest">U ontvangt van ons geen factuur voor deze aanvraag.</p>
                                </div>
                            ) : (
                                <dl className="space-y-2 text-sm">
                                    <div className="flex items-baseline justify-between gap-6">
                                        <dt className="text-ink-subtlest">Factuur aan:</dt>
                                        <dd className="font-medium text-ink">{AanvraagDetails?.klantId?.naam}</dd>
                                    </div>
                                    <div className="flex items-baseline justify-between gap-6">
                                        <dt className="text-ink-subtlest">Bedrag exclusief BTW:</dt>
                                        <dd className="font-medium tabular-nums text-ink">€{formatNumber(AanvraagDetails?.exclusiveBTW || '0')}</dd>
                                    </div>
                                    <div className="flex items-baseline justify-between gap-6">
                                        <dt className="text-ink-subtlest">Bedrag inclusief BTW:</dt>
                                        <dd className="font-medium tabular-nums text-ink">€{formatNumber(AanvraagDetails?.totaal || '0')}</dd>
                                    </div>
                                </dl>
                            )}
                        </Section>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ClientAanvraagDetailsPage;
